import {BrowserModule} from '@angular/platform-browser';
import {ErrorHandler, LOCALE_ID, NgModule} from '@angular/core';
import {AppComponent} from './app.component';
import {ModuleRouting} from './app.routing';
import {SharedModule} from './shared/shared.module';
import {GlobalErrorHandlerService} from './shared/error-handler/global-error-handler.service';
import {registerLocaleData} from '@angular/common';
import localedeCH from '@angular/common/locales/de-CH';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {HttpErrorReportingInterceptor} from './shared/http-interceptors/http-error-reporting.interceptor';
import * as Sentry from '@sentry/browser';
import {environment} from "../environments/environment";
import {HttpProgressInterceptor} from './shared/http-interceptors/http-progress.interceptor';
import {ProgressBarComponent} from './shared/components/progress-bar/progress-bar.component';
import {HttpSuccessToastInterceptor} from './shared/http-interceptors/http-success-toast.interceptor';
import {authHttpInterceptorFn, provideAuth0} from "@auth0/auth0-angular";
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {providePrimeNG} from 'primeng/config';
import {ToastModule} from 'primeng/toast';
import {MessageService} from 'primeng/api';
import Aura from '@primeng/themes/aura';
import {definePreset} from '@primeng/themes';

const MercatusPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '#e6f7f4',
            100: '#b3e8de',
            200: '#80d9c8',
            300: '#4dcab2',
            400: '#26bea0',
            500: '#16A084',
            600: '#128a72',
            700: '#0e7460',
            800: '#0a5e4e',
            900: '#06483c',
            950: '#032820'
        },
        formField: {
            paddingX: '0.75rem',
            paddingY: '0.375rem'
        },
        colorScheme: {
            light: {
                primary: {
                    color: '#16A084',
                    contrastColor: '#ffffff',
                    hoverColor: '#128a72',
                    activeColor: '#0e7460'
                },
                highlight: {
                    background: '#9a7ab3',
                    focusBackground: '#b399c7',
                    color: '#ffffff',
                    focusColor: '#ffffff'
                },
                formField: {
                    focusBorderColor: '#735093'
                }
            }
        }
    },
    components: {
        datatable: {
            headerCell: {
                background: '#3a5169',
                hoverBackground: '#3a5169',
                color: '#ffffff'
            },
            sortIcon: {
                color: '#ffffff'
            }
        },
        checkbox: {
            root: {
                checkedBackground: '#735093',
                checkedBorderColor: '#735093',
                checkedHoverBackground: '#5e4078',
                checkedHoverBorderColor: '#5e4078'
            }
        },
        radiobutton: {
            root: {
                checkedBackground: '#735093',
                checkedBorderColor: '#735093',
                checkedHoverBackground: '#5e4078',
                checkedHoverBorderColor: '#5e4078'
            }
        },
        autocomplete: {
            chip: {
                focusBackground: '#9a7ab3'
            }
        },
        togglebutton: {
            root: {
                background: '#3a5169',
                checkedBackground: '#3a5169',
                hoverBackground: '#4a6179',
                color: '#ffffff',
                checkedColor: '#ffffff',
                borderColor: '#3a5169',
                checkedBorderColor: '#3a5169'
            }
        },
        paginator: {
            root: {
                background: '#ffffff'
            }
        },
        accordion: {
            header: {
                background: '#F8F8F8',
                hoverBackground: '#F8F8F8',
                activeBackground: '#F8F8F8',
                color: '#000000',
                hoverColor: '#000000',
                activeColor: '#000000'
            }
        },
        dialog: {
            root: {
                borderRadius: '3px'
            },
            title: {
                fontSize: '14px',
                fontWeight: '700'
            }
        }
    }
});

Sentry.init({
    dsn: "https://2a5eb9c8fdc049bf8d8c9778e900776b@sentry.io/1420761",
    release: environment.VERSION
});

registerLocaleData(localedeCH);

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        SharedModule.forRoot(),
        ModuleRouting,
        BrowserModule,
        ProgressBarComponent,
        ToastModule
    ],
    providers: [
        MessageService,
        { provide: LOCALE_ID, useValue: 'de-CH' },
        { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpProgressInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorReportingInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpSuccessToastInterceptor,
            multi: true,
        },
        provideAuth0({
            domain: environment.auth0.domain,
            clientId: environment.auth0.clientId,
            authorizationParams: {
                redirect_uri: window.location.origin,
                audience: environment.auth0.audience
            },
            useRefreshTokens: true,
            cacheLocation: 'localstorage',
            errorPath: '/login',
            httpInterceptor: {
                allowedList: [`${environment.apiUrl}/*`]
            }
        }),
        provideHttpClient(withXhr(), withInterceptors([authHttpInterceptorFn]), withInterceptorsFromDi()),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: MercatusPreset,
                options: {
                    darkModeSelector: false
                }
            },
            translation: {
                dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
                dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
                dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
                monthNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
                monthNamesShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
            }
        }),
    ]
})
export class AppModule {
}
